import React from 'react';
import { Trophy, ArrowRight } from 'lucide-react';
import { CopyCode } from './CopyCode';
import { RankBadge } from './RankBadge';
import Logo from './Logo';
import { SITE, PRIZE_SPLIT } from '../mock';

const fmt = (n) => Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
const prizeFor = (rank) => (PRIZE_SPLIT.find((p) => p.rank === rank) || {}).prize || 0;

// Faint original line-art casino decor (cyan outlines)
function Decor() {
  const stroke = 'rgba(0,217,255,0.22)';
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden" aria-hidden="true">
      <svg className="absolute -left-10 top-24 w-40 h-40 ar-float-slow hidden md:block" viewBox="0 0 100 100" fill="none">
        <rect x="18" y="14" width="46" height="66" rx="8" stroke={stroke} strokeWidth="2" transform="rotate(-12 40 47)" />
        <path d="M40 40 C33 48 33 55 40 55 C47 55 47 48 40 40 Z M40 55 l-3 8 h6 z" stroke={stroke} strokeWidth="2" transform="rotate(-12 40 47)" />
      </svg>
      <svg className="absolute right-6 top-16 w-28 h-28 ar-float hidden md:block" viewBox="0 0 100 100" fill="none">
        <rect x="18" y="18" width="64" height="64" rx="14" stroke={stroke} strokeWidth="2" />
        <circle cx="36" cy="36" r="4" fill={stroke} /><circle cx="64" cy="36" r="4" fill={stroke} />
        <circle cx="50" cy="50" r="4" fill={stroke} /><circle cx="36" cy="64" r="4" fill={stroke} /><circle cx="64" cy="64" r="4" fill={stroke} />
      </svg>
      <svg className="absolute -right-8 bottom-6 w-36 h-36 ar-float-slow" viewBox="0 0 100 100" fill="none">
        <circle cx="50" cy="50" r="34" stroke={stroke} strokeWidth="2" />
        <circle cx="50" cy="50" r="24" stroke={stroke} strokeWidth="1.4" strokeDasharray="3 7" />
      </svg>
    </div>
  );
}

function PreviewRow({ rank, entry }) {
  return (
    <div className="flex items-center gap-3 py-2.5">
      <span className="w-6 text-center font-display font-extrabold text-sm" style={{ color: rank <= 3 ? 'var(--ar-cyan)' : 'var(--ar-muted)' }}>{rank}</span>
      <span className="flex-1 min-w-0 flex items-center gap-2"><RankBadge src={entry.rankLevelImage} size={20} testId={`hero-badge-${rank}`} /><span className="truncate font-display font-semibold text-sm">{entry.username}</span></span>
      <span className="text-sm text-muted font-display tabular-nums whitespace-nowrap">${fmt(entry.weightedWagered)}</span>
      <span className="w-16 text-right font-display font-extrabold text-cyan tabular-nums">${fmt(prizeFor(rank))}</span>
    </div>
  );
}

export default function Hero({ lb }) {
  const { status, entries } = lb;
  const top5 = (entries || []).slice(0, 5);
  const hasData = status === 'ok' || status === 'stale';

  return (
    <section id="home" className="relative pt-28 sm:pt-32 lg:pt-36 pb-14 sm:pb-20 overflow-hidden">
      <Decor />
      <div className="ar-container">
        <div className="grid lg:grid-cols-2 gap-10 lg:gap-14 items-center">
          {/* Left */}
          <div className="text-center lg:text-left">
            <div className="flex items-center gap-3 justify-center lg:justify-start mb-5 hero-enter hero-enter-1">
              <Logo size={56} glow={false} className="w-12 h-12 sm:w-14 sm:h-14" />
              <span className="ar-eyebrow">ASIANRISK <span className="text-muted">×</span> Roobet</span>
            </div>

            <h1 className="ar-heading text-4xl sm:text-5xl md:text-6xl mb-5 hero-enter hero-enter-2">
              <span className="text-cyan">$500 MONTHLY</span><br />LEADERBOARD.
            </h1>

            <p className="text-base sm:text-lg text-muted max-w-xl mx-auto lg:mx-0 mb-7 leading-relaxed hero-enter hero-enter-3">
              Live Roobet streams on Kick, a $500 total monthly prize pool for the top five, and a Discord community. Follow the standings and take part with code ASIANRISK.
            </p>

            <div className="flex flex-col sm:flex-row gap-3 justify-center lg:justify-start mb-6 hero-enter hero-enter-4">
              <a href={SITE.roobetRef} target="_blank" rel="noopener noreferrer" className="btn-cyan" data-testid="hero-open-roobet-btn"><img src="/assets/roobet-chip-navy.png" alt="" aria-hidden="true" className="h-5 w-5 object-contain" /> OPEN ROOBET</a>
              <a href="#leaderboard" className="btn-gold" data-testid="hero-view-leaderboard-btn"><Trophy size={18} aria-hidden="true" /> VIEW LEADERBOARD</a>
            </div>

            <div className="inline-flex items-center gap-3 ar-panel px-4 py-2.5 hero-enter hero-enter-4">
              <span className="text-xs text-muted font-display tracking-wider">CODE</span>
              <span className="font-display font-extrabold text-cyan tracking-wide">{SITE.code}</span>
              <CopyCode testId="hero-copy-code-btn" />
            </div>
          </div>

          {/* Right: live top-5 preview */}
          <div className="hero-enter hero-enter-3">
            <div className="ar-panel p-5 sm:p-6" data-testid="hero-top5">
              <div className="flex items-center justify-between mb-2">
                <span className="ar-eyebrow">Current Top 5</span>
                <span className="text-[11px] text-muted font-display" data-testid="hero-top5-status">{status === 'stale' ? 'Possibly out of date' : status === 'ok' ? 'Live · Roobet' : ''}</span>
              </div>
              <div className="ar-divider mb-1" />
              <div className="flex items-center gap-3 py-2 text-[10px] tracking-[0.15em] text-muted font-display uppercase">
                <span className="w-6 text-center">#</span>
                <span className="flex-1">Player</span>
                <span>Weighted</span>
                <span className="w-16 text-right">Prize</span>
              </div>

              {status === 'loading' && [0, 1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-3 py-3">
                  <div className="ar-skel w-6 h-4" />
                  <div className="ar-skel flex-1 h-4" />
                  <div className="ar-skel w-14 h-4" />
                </div>
              ))}

              {status === 'unavailable' && (
                <p className="text-sm text-muted py-6 text-center">Standings are unavailable right now. They'll appear automatically.</p>
              )}

              {hasData && top5.length === 0 && (
                <p className="text-sm text-muted py-6 text-center">No wagers yet this month — be the first with code {SITE.code}.</p>
              )}

              {hasData && top5.map((e, i) => (
                <React.Fragment key={i}>
                  {i > 0 && <div className="h-px bg-white/5" />}
                  <PreviewRow rank={i + 1} entry={e} />
                </React.Fragment>
              ))}

              <a href="#leaderboard" data-testid="hero-full-leaderboard-link" className="mt-3 flex items-center justify-center gap-2 text-sm font-display font-bold text-cyan hover:gap-3 transition-all">
                View full leaderboard <ArrowRight size={16} />
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
